import React from 'react';
import PropTypes from 'prop-types';
import useReviewEditor from './useReviewEditor';
import useRatingsEditor from './useRatingsEditor';
import useShareTargets from './useShareTargets';
import useEditorController from './useEditorController';
import useNominationTargets from './useNominationTargets';

const UserReviewEditorContext = React.createContext({
	isLoading: true,
	reviewEditor: {},
	ratingsEditor: {},
	editorController: {},
	shareTargetsEditor: {},
	nominationTargetsEditor: {},
});

export const useUserReviewEditor = () => {
	return React.useContext(UserReviewEditorContext);
};

UserReviewEditorProvider.propTypes = {
	sku: PropTypes.string,
	onUpdate: PropTypes.func,
	children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
	shareTargetsOptions: PropTypes.shape({
		doIncludeGenreCommunities: PropTypes.bool,
	}),
};

export default function UserReviewEditorProvider({
	sku,
	children,
	onUpdate,
	shareTargetsOptions,
}) {
	const reviewEditor = useReviewEditor(sku);
	const ratingsEditor = useRatingsEditor(sku);

	const shareTargetsEditor = useShareTargets(sku, shareTargetsOptions);
	const nominationTargetsEditor = useNominationTargets(sku);

	const editorController = useEditorController(
		sku,
		onUpdate,
		reviewEditor,
		ratingsEditor,
		shareTargetsEditor,
		nominationTargetsEditor,
	);

	const isLoading = () => {
		return (
			reviewEditor.isLoading() ||
			ratingsEditor.isLoading() ||
			shareTargetsEditor.isLoading() ||
			nominationTargetsEditor.isLoading()
		);
	};

	const providerValue = {
		isLoading,
		reviewEditor,
		ratingsEditor,
		editorController,
		shareTargetsEditor,
		nominationTargetsEditor,
	};

	return (
		<UserReviewEditorContext.Provider value={providerValue}>
			{typeof children === 'function'
				? children(providerValue)
				: children}
		</UserReviewEditorContext.Provider>
	);
}
